import React, {useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import Icon from "../icon";
import {useTranslate} from "../../useTranslate";
import useSearch from "./use-search";
import SearchResultItem from "./search-result-item";
import {SEARCH_MIN_LENGTH, SEARCH_TYPES, resultTarget, searchPageUrl, searchTypeMeta} from "./search-config";
import "./global-search.css";

// The search bar of the app header (boss's task «ساخت نوار ناوبری», docs/tasks/21-global-search.md):
// - Ctrl+K (⌘K on a Mac) focuses it from anywhere; typing opens the results, Esc or a click outside closes them and
//   keeps the text for later.
// - results are grouped (messages, tasks, customers, projects, goals, teams), up to 5 per kind, with quick tabs
//   to see one kind only; ↑ ↓ Enter work from the keyboard.
// - the footer opens the full /search page with filters.

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || "");

function GlobalSearch() {
    const translate = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const [text, setText] = useState("");
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("all");
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const query = text.trim();
    const {loading, error, data} = useSearch({q: query, pageSize: 5});

    // Ctrl+K / ⌘K from anywhere
    useEffect(() => {
        const onKeyDown = (event) => {
            if ((event.ctrlKey || event.metaKey) && !event.altKey && String(event.key).toLowerCase() === "k") {
                event.preventDefault();
                inputRef.current?.focus();
                inputRef.current?.select();
                if (inputRef.current?.value.trim()) setOpen(true);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);

    // a click outside closes the results (the text stays)
    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            if (!containerRef.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("touchstart", onPointerDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("touchstart", onPointerDown);
        };
    }, [open]);

    // a new page closes the results
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const groups = useMemo(() => {
        const list = data?.groups || [];
        return SEARCH_TYPES
            .map((meta) => list.find((group) => group.type === meta.type))
            .filter((group) => group && group.items?.length);
    }, [data]);
    const visibleGroups = tab === "all" ? groups : groups.filter((group) => group.type === tab);
    const flatItems = useMemo(() => visibleGroups.flatMap((group) => group.items), [visibleGroups]);
    const total = tab === "all" ? data?.total || 0 : groups.find((group) => group.type === tab)?.total || 0;

    useEffect(() => {
        setActiveIndex(-1);
    }, [data, tab]);

    const openItem = (item) => {
        const target = resultTarget(item, location);
        if (!target) return;
        setOpen(false);
        inputRef.current?.blur();
        navigate(target);
    };
    const openSearchPage = () => {
        setOpen(false);
        inputRef.current?.blur();
        navigate(searchPageUrl(query, tab));
    };

    const onKeyDown = (event) => {
        if (event.key === "Escape") {
            setOpen(false);
            inputRef.current?.blur();
            return;
        }
        if (!open && ["ArrowDown", "Enter"].includes(event.key) && query) setOpen(true);
        if (event.key === "ArrowDown" && flatItems.length) {
            event.preventDefault();
            setActiveIndex((index) => (index + 1) % flatItems.length);
        } else if (event.key === "ArrowUp" && flatItems.length) {
            event.preventDefault();
            setActiveIndex((index) => (index <= 0 ? flatItems.length - 1 : index - 1));
        } else if (event.key === "Enter") {
            event.preventDefault();
            if (activeIndex >= 0 && flatItems[activeIndex]) openItem(flatItems[activeIndex]);
            else if (query.length >= SEARCH_MIN_LENGTH) openSearchPage();
        }
    };

    useEffect(() => {
        if (activeIndex < 0) return;
        document.getElementById(`global-search-option-${activeIndex}`)?.scrollIntoView({block: "nearest"});
    }, [activeIndex]);

    const showDropdown = open && query.length > 0;
    let position = -1;

    return (
        <div className="global-search" ref={containerRef}>
            <div className="global-search-field">
                <Icon icon="mi mi:search" className="global-search-field-icon"/>
                <input
                    ref={inputRef}
                    type="text"
                    className="global-search-input"
                    value={text}
                    placeholder={translate("search.placeholder")}
                    aria-label={translate("search.placeholder")}
                    role="combobox"
                    aria-expanded={showDropdown}
                    aria-controls="global-search-results"
                    aria-activedescendant={activeIndex >= 0 ? `global-search-option-${activeIndex}` : undefined}
                    autoComplete="off"
                    onChange={(event) => {
                        setText(event.target.value);
                        setOpen(Boolean(event.target.value.trim()));
                    }}
                    onFocus={() => {
                        if (text.trim()) setOpen(true);
                    }}
                    onKeyDown={onKeyDown}
                />
                {loading && query.length >= SEARCH_MIN_LENGTH
                    ? <i className="pi pi-spin pi-spinner global-search-shortcut text-500"/>
                    : text
                        ? <button type="button" className="global-search-clear" aria-label={translate("search.clear")} onClick={() => {
                            setText("");
                            setOpen(false);
                            inputRef.current?.focus();
                        }}><i className="pi pi-times"/></button>
                        : <span className="global-search-shortcut" aria-hidden="true"><kbd>{isMac ? "⌘" : "Ctrl"}</kbd><kbd>K</kbd></span>}
            </div>

            <div id="global-search-results" className={`global-search-dropdown ${showDropdown ? "is-open" : ""}`} role="listbox">
                <div className="global-search-tabs">
                    {[{type: "all", label: "search.type.all"}, ...SEARCH_TYPES].map((item) => {
                        const count = item.type === "all" ? data?.total : groups.find((group) => group.type === item.type)?.total;
                        return (
                            <button
                                key={item.type}
                                type="button"
                                className={`global-search-tab ${tab === item.type ? "is-active" : ""}`}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => setTab(item.type)}
                            >
                                {translate(item.label)}
                                {!!count && <span className="global-search-tab-count">{count}</span>}
                            </button>
                        );
                    })}
                </div>

                <div className="global-search-body">
                    {query.length < SEARCH_MIN_LENGTH ? (
                        <div className="global-search-empty">{translate("search.min-length")}</div>
                    ) : error ? (
                        <div className="global-search-empty">{translate("search.error")}</div>
                    ) : !data ? (
                        <div className="global-search-empty"><i className="pi pi-spin pi-spinner"/></div>
                    ) : !visibleGroups.length ? (
                        <div className="global-search-empty">{translate("search.empty").replace("{query}", query)}</div>
                    ) : visibleGroups.map((group) => (
                        <div key={group.type} className="global-search-group">
                            <div className="global-search-group-head">
                                <span>{translate(searchTypeMeta(group.type).group)}</span>
                                <span className="global-search-group-count">{group.total} {translate("search.results")}</span>
                            </div>
                            {group.items.map((item) => {
                                position += 1;
                                const index = position;
                                return (
                                    <SearchResultItem
                                        key={`${item.type}-${item.id}`}
                                        id={`global-search-option-${index}`}
                                        item={item}
                                        translate={translate}
                                        active={index === activeIndex}
                                        onHover={() => setActiveIndex(index)}
                                        onSelect={openItem}
                                    />
                                );
                            })}
                        </div>
                    ))}
                </div>

                {query.length >= SEARCH_MIN_LENGTH && total > 0 && (
                    <button type="button" className="global-search-footer" onMouseDown={(event) => event.preventDefault()} onClick={openSearchPage}>
                        {translate("search.see-all").replace("{count}", total)}
                        <Icon icon="mi mi:arrow_back" className="text-sm"/>
                    </button>
                )}
            </div>
        </div>
    );
}

export default GlobalSearch;
